<template>
    <div class="border-gray-100 border overflow-hidden mb-4 files-card">
        <div class="px-3 py-2 border-bottom border-gray-100 d-flex align-items-center justify-content-between gap-12">
            <div class="d-flex align-items-center gap-2">
                <span class="text-ashore-black fs-14 font-weight-bold">Files</span>
                <span v-if="files.length > 0" class="fs-14 text-gray-500">{{ files.length }}</span>
            </div>
            <div class="d-flex align-items-center gap-2">
                <button
                    v-if="jobId && downloadableFiles.length > 0"
                    class="upload-btn btn d-flex align-items-center gap-2 fw-medium fs-12 px-12 py-2"
                    @click="downloadAllZip">
                    Download all
                </button>
                <button
                    v-if="jobId && proofableFiles.length > 0"
                    class="upload-btn btn d-flex align-items-center gap-2 fw-medium fs-12 px-12 py-2"
                    @click="openCreateProofModal">
                    Create proof
                </button>
                <button
                    v-if="files.length > 0"
                    class="upload-btn btn d-flex align-items-center gap-2 fw-medium fs-12 px-12 py-2"
                    @click="pickFiles">
                    <img src="/img/plus-light.svg" width="12" alt="" />
                    Upload
                </button>
            </div>
        </div>

        <div
            class="files-body"
            :class="{ 'is-drag-over': isDragOver }"
            @dragenter.prevent="onDragEnter"
            @dragover.prevent="onDragEnter"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
            <!-- Loading -->
            <div v-if="isLoading" class="text-center py-4">
                <div class="spinner-border text-gray-400" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>

            <!-- Empty -->
            <button
                v-else-if="files.length === 0"
                type="button"
                class="files-empty"
                @click="pickFiles">
                <span class="files-empty__glyph" aria-hidden="true">
                    <img src="/img/icon-attachment.svg" width="20" alt="" />
                </span>
                <span class="files-empty__title">Drop files here, or click to upload</span>
                <span class="files-empty__hint">Up to {{ maxSizeLabel }} per file</span>
            </button>

            <!-- File list -->
            <ul v-else class="files-list list-unstyled mb-0">
                <li
                    v-for="file in files"
                    :key="file.id || file.tempId"
                    class="file-row d-flex align-items-center gap-12 px-3 py-12">
                    <span class="file-row__glyph flex-shrink-0">
                        <img src="/img/icon-attachment.svg" width="16" alt="" />
                    </span>
                    <div class="file-row__meta flex-fill min-w-0">
                        <a
                            v-if="!file.uploading && !file.failed && !file.pending && file.url"
                            :href="file.url"
                            target="_blank"
                            rel="noopener"
                            class="file-row__name d-block text-truncate text-ashore-black fs-14"
                            :title="file.name">
                            {{ file.name }}
                        </a>
                        <span
                            v-else
                            class="file-row__name d-block text-truncate fs-14"
                            :class="file.failed ? 'text-danger' : 'text-gray-500'"
                            :title="file.name">
                            {{ file.name }}
                        </span>
                        <span class="file-row__sub d-block fs-12 text-gray-500 mt-1">
                            <template v-if="file.preset">From submission &middot; {{ formatBytes(file.bytes) }}</template>
                            <template v-else-if="file.pending">Will upload on save</template>
                            <template v-else-if="file.uploading">Uploading…</template>
                            <template v-else-if="file.failed">Upload failed</template>
                            <template v-else>{{ formatBytes(file.bytes) }}</template>
                        </span>
                    </div>

                    <div v-if="!file.uploading && !file.preset" class="dropdown dropdown-wrapper-js" draggable="false">
                        <button
                            class="btn p-1 dropdown-toggle-js"
                            @click.stop="toggleMenu(file)">
                            <img src="/img/dots-horizontal.svg" width="14" alt="" />
                        </button>
                        <div
                            class="dropdown-menu dropdown-js rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden"
                            :class="{ show: openMenuId === (file.id || file.tempId) }"
                            style="min-width: 180px;"
                            @click.stop>
                            <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush">
                                <li v-if="!file.failed && !file.pending" class="p-0 bg-white">
                                    <a
                                        :href="fileDownloadUrl(file)"
                                        class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click="closeMenu">
                                        Download
                                    </a>
                                </li>
                                <li class="p-0 bg-white">
                                    <span
                                        class="dropdown-item text-danger d-flex align-items-center gap-12 py-2 px-3"
                                        @click="confirmRemove(file)">
                                        {{ (file.failed || file.pending) ? 'Remove' : 'Delete' }}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </li>
            </ul>

            <div v-if="isDragOver" class="drop-overlay" aria-hidden="true">
                <div class="drop-overlay__inner">
                    <img src="/img/icon-attachment.svg" width="22" alt="" />
                    <span>Drop to upload</span>
                </div>
            </div>
        </div>

        <input
            ref="fileInput"
            type="file"
            multiple
            class="d-none"
            @change="onFileChange" />

        <b-modal id="createProofFromJobModal" centered hide-footer hide-header size="md">
            <div class="py-2">
                <h5 class="modal-heading mb-2">Create proof from files</h5>
                <p class="text-muted fs-14 mb-3">
                    Select the files to include in the new proof.
                </p>
                <ul v-if="proofableFiles.length > 0" class="list-unstyled mb-0 proof-pick-list">
                    <li
                        v-for="file in proofableFiles"
                        :key="file.id"
                        class="proof-pick-row">
                        <input
                            :id="`proof-pick-${file.id}`"
                            type="checkbox"
                            class="proof-pick-checkbox"
                            :checked="selectedProofFileIds.includes(file.id)"
                            @change="toggleProofFile(file.id)" />
                        <label
                            :for="`proof-pick-${file.id}`"
                            class="proof-pick-name fs-14 text-ashore-black"
                            :title="file.name">
                            {{ file.name }}
                        </label>
                        <span class="proof-pick-size fs-12 text-gray-500">{{ formatBytes(file.bytes) }}</span>
                    </li>
                </ul>
                <p v-else class="text-muted fs-14 mb-0">No uploaded files available.</p>
            </div>
            <div class="d-flex justify-content-end gap-2 mt-4">
                <button class="btn cancel-btn fw-normal" @click="cancelCreateProof">Cancel</button>
                <button
                    class="btn btn-success fw-normal"
                    :disabled="selectedProofFileIds.length === 0"
                    @click="confirmCreateProof">
                    Continue
                </button>
            </div>
        </b-modal>

        <b-modal id="deleteJobFileModal" centered hide-footer hide-header>
            <div class="py-2">
                <h5 class="modal-heading mb-2">Delete file?</h5>
                <p class="text-muted fs-14 mb-0">
                    Are you sure you want to delete
                    <span class="fw-medium text-ashore-black">{{ fileToDelete ? fileToDelete.name : '' }}</span>?
                    This action cannot be undone.
                </p>
            </div>
            <div class="d-flex justify-content-end gap-2 mt-4">
                <button class="btn cancel-btn fw-normal" @click="cancelDelete">Cancel</button>
                <button class="btn btn-danger fw-normal" @click="performDelete">Delete</button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import EnvService from "../../env.settings";
import { VARIANT } from "../../utils/constants";

const MAX_FILE_BYTES = 256 * 1024 * 1024;

export default {
    name: "JobFiles",
    props: {
        jobId: {
            type: Number,
            default: null,
        },
        presetFiles: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            files: [],
            isLoading: true,
            isDragOver: false,
            openMenuId: null,
            dragCounter: 0,
            fileToDelete: null,
            selectedProofFileIds: [],
            isCreatingProof: false,
        };
    },
    computed: {
        maxSizeLabel() {
            return "256 MB";
        },
        proofableFiles() {
            return this.files.filter(
                (f) => f.id && f.url && !f.uploading && !f.failed && !f.pending && !f.preset
            );
        },
        downloadableFiles() {
            return this.files.filter(
                (f) => f.id && !f.uploading && !f.failed && !f.pending && !f.preset
            );
        },
    },
    mounted() {
        this.loadFiles();
        document.addEventListener("click", this.handleOutsideClick);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleOutsideClick);
    },
    methods: {
        formatBytes(bytes) {
            if (bytes == null || bytes <= 0) return "";
            const units = ["B", "KB", "MB", "GB"];
            let i = 0;
            let n = bytes;
            while (n >= 1024 && i < units.length - 1) {
                n /= 1024;
                i++;
            }
            return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
        },

        // Download links route through the API (which sets an attachment
        // content-disposition) so files save to disk instead of opening inline.
        apiBase() {
            return EnvService.instance.apiHost.replace(/\/+$/, "");
        },
        fileDownloadUrl(file) {
            const token = encodeURIComponent(this.$A.Auth.apiToken.token);
            return `${this.apiBase()}/jobs/${this.jobId}/files/${file.id}/download?access_token=${token}`;
        },
        downloadAllZip() {
            const token = encodeURIComponent(this.$A.Auth.apiToken.token);
            const url = `${this.apiBase()}/jobs/${this.jobId}/files/zip?access_token=${token}`;
            const a = document.createElement("a");
            a.href = url;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },

        async loadFiles() {
            if (!this.jobId) {
                if (Array.isArray(this.presetFiles) && this.presetFiles.length) {
                    this.files = this.presetFiles.map((f, i) => ({
                        id: f.id != null ? `preset-${f.id}` : `preset-${i}`,
                        name: f.name,
                        bytes: f.size || f.bytes || 0,
                        url: f.url || null,
                        preset: true,
                    }));
                }
                this.isLoading = false;
                return;
            }
            this.isLoading = true;
            try {
                const result = await this.$A.JobService.getJobFiles(this.jobId);
                this.files = Array.isArray(result) ? result : [];
            } catch (e) {
                console.error("Failed to load job files:", e);
                this.files = [];
            } finally {
                this.isLoading = false;
            }
        },

        // Files queued in create mode (no jobId). Parent grabs these and uploads after job creation.
        extractPending() {
            return this.files
                .filter((f) => f.pending && f._nativeFile)
                .map((f) => ({ file: f._nativeFile, name: f.name }));
        },

        pickFiles() {
            this.$refs.fileInput.value = "";
            this.$refs.fileInput.click();
        },

        onFileChange(event) {
            const list = Array.from(event.target.files || []);
            if (list.length) this.uploadFiles(list);
        },

        onDragEnter() {
            this.dragCounter += 1;
            this.isDragOver = true;
        },

        onDragLeave() {
            this.dragCounter = Math.max(0, this.dragCounter - 1);
            if (this.dragCounter === 0) this.isDragOver = false;
        },

        onDrop(event) {
            this.dragCounter = 0;
            this.isDragOver = false;
            const list = Array.from(event.dataTransfer?.files || []);
            if (list.length) this.uploadFiles(list);
        },

        async uploadFiles(fileList) {
            const valid = [];
            for (const f of fileList) {
                if (f.size > MAX_FILE_BYTES) {
                    this.$toast(
                        `${f.name} exceeds the ${this.maxSizeLabel} limit`,
                        { title: "File too large", variant: VARIANT.DANGER }
                    );
                    continue;
                }
                valid.push(f);
            }
            if (!valid.length) return;

            const queueOnly = !this.jobId;
            const optimistic = valid.map((f) => ({
                tempId: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                name: f.name,
                url: null,
                id: null,
                bytes: f.size,
                uploading: !queueOnly,
                failed: false,
                pending: queueOnly,
                _nativeFile: queueOnly ? f : undefined,
            }));
            this.files = [...optimistic, ...this.files];

            if (queueOnly) return;

            await Promise.all(
                valid.map((file, i) => this.uploadOne(file, optimistic[i].tempId))
            );
        },

        async uploadOne(file, tempId) {
            const idx = () => this.files.findIndex((f) => f.tempId === tempId);
            try {
                const token = this.$A.Auth.apiToken.token;
                const base = EnvService.instance.apiHost.replace(/\/+$/, "");
                const url = `${base}/jobs/${this.jobId}/files?access_token=${encodeURIComponent(token)}`;

                const form = new FormData();
                form.append("file", file, file.name);

                const res = await fetch(url, { method: "POST", body: form });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const uploaded = data ? Object.values(data)[0] : null;
                if (!uploaded) throw new Error("Empty response");

                const i = idx();
                if (i !== -1) {
                    this.$set(this.files, i, {
                        id: uploaded.id,
                        name: uploaded.name,
                        url: uploaded.url,
                        bytes: uploaded.bytes != null ? uploaded.bytes : file.size,
                        uploading: false,
                        failed: false,
                    });
                }
            } catch (e) {
                console.error("Job file upload failed:", e);
                const i = idx();
                if (i !== -1) {
                    this.$set(this.files, i, {
                        ...this.files[i],
                        uploading: false,
                        failed: true,
                    });
                }
                this.$toast(`Failed to upload ${file.name}`, {
                    title: "Upload error",
                    variant: VARIANT.DANGER,
                });
            }
        },

        toggleMenu(file) {
            const key = file.id || file.tempId;
            this.openMenuId = this.openMenuId === key ? null : key;
        },

        closeMenu() {
            this.openMenuId = null;
        },

        handleOutsideClick() {
            this.openMenuId = null;
        },

        confirmRemove(file) {
            this.closeMenu();
            this.fileToDelete = file;
            this.$bvModal.show("deleteJobFileModal");
        },

        cancelDelete() {
            this.$bvModal.hide("deleteJobFileModal");
            this.fileToDelete = null;
        },

        inferMimeType(name) {
            const ext = (name || "").split(".").pop().toLowerCase();
            const map = {
                pdf: "application/pdf",
                png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif",
                webp: "image/webp", svg: "image/svg+xml", bmp: "image/bmp", tiff: "image/tiff", tif: "image/tiff",
                mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm", avi: "video/x-msvideo",
                mp3: "audio/mpeg", wav: "audio/wav", m4a: "audio/mp4", ogg: "audio/ogg",
                html: "text/html", htm: "text/html",
            };
            return map[ext] || "";
        },

        openCreateProofModal() {
            this.selectedProofFileIds = this.proofableFiles.map((f) => f.id);
            this.$bvModal.show("createProofFromJobModal");
        },

        toggleProofFile(id) {
            const i = this.selectedProofFileIds.indexOf(id);
            if (i === -1) this.selectedProofFileIds.push(id);
            else this.selectedProofFileIds.splice(i, 1);
        },

        cancelCreateProof() {
            this.$bvModal.hide("createProofFromJobModal");
        },

        confirmCreateProof() {
            if (this.isCreatingProof || this.selectedProofFileIds.length === 0) return;
            const picked = this.proofableFiles.filter((f) =>
                this.selectedProofFileIds.includes(f.id)
            );
            if (picked.length === 0) return;
            this.isCreatingProof = true;
            window.$A = window.$A || {};
            window.$A.filesForProof = picked.map((f) => ({
                name: f.name,
                sizeBytes: f.bytes || 0,
                mimeType: this.inferMimeType(f.name),
                urlSource: f.url,
            }));
            window.$A.proofJobIdToLink = this.jobId;
            this.$bvModal.hide("createProofFromJobModal");
            this.$router.push("/create-proof");
        },

        async performDelete() {
            const file = this.fileToDelete;
            this.$bvModal.hide("deleteJobFileModal");
            this.fileToDelete = null;
            if (!file) return;

            const snapshot = this.files;
            this.files = this.files.filter(
                (f) => (f.id || f.tempId) !== (file.id || file.tempId)
            );

            if (!file.id) return;

            try {
                await this.$A.JobService.deleteJobFile(this.jobId, file.id);
            } catch (e) {
                console.error("Failed to delete job file:", e);
                this.files = snapshot;
            }
        },
    },
};
</script>

<style scoped>
.files-card {
    border-radius: 6px;
}

.files-card:has(.dropdown-menu.show) {
    overflow: visible !important;
}

.file-row {
    position: relative;
}
.file-row:has(.dropdown-menu.show) {
    z-index: 1100;
}

.upload-btn {
    border: 1px solid #E1E3E8;
    background: #FFFFFF;
    color: #4B5563;
}
.upload-btn:hover {
    border-color: #019256;
    color: #019256;
}

.files-body {
    position: relative;
    transition: background 0.15s ease;
}
.files-body.is-drag-over {
    background: rgba(1, 146, 86, 0.04);
}

/* Empty state */
.files-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: calc(100% - 24px);
    margin: 12px;
    padding: 28px 16px;
    border: 1.5px dashed #E1E3E8;
    border-radius: 10px;
    background:
        radial-gradient(120% 80% at 50% 0%, rgba(1, 146, 86, 0.05), transparent 70%),
        #FCFCFD;
    color: #6B7280;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
    appearance: none;
    outline: none;
}
.files-empty:hover {
    border-color: #019256;
    background:
        radial-gradient(120% 80% at 50% 0%, rgba(1, 146, 86, 0.07), transparent 65%),
        #FFFFFF;
    transform: translateY(-1px);
}
.files-empty__glyph {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(1, 146, 86, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.04);
}
.files-empty__glyph img {
    opacity: 0.7;
}
.files-empty__title {
    font-size: 13px;
    font-weight: 500;
    color: #4B5563;
}
.files-empty__hint {
    font-size: 12px;
    color: #9CA3AF;
}

/* File rows */
.files-list {
    padding: 0;
}
.file-row {
    transition: background 0.12s ease;
}
.file-row + .file-row {
    border-top: 1px solid #F4F4F6;
}
.file-row:hover {
    background: #F9FAFB;
}
.file-row__glyph {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #F3F4F6;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.file-row__glyph img {
    opacity: 0.7;
}
.file-row__name {
    text-decoration: none;
}
.file-row__name:hover {
    text-decoration: underline;
}

/* Drag overlay */
.drop-overlay {
    position: absolute;
    inset: 6px;
    border: 1.5px dashed #019256;
    border-radius: 8px;
    background: rgba(1, 146, 86, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}
.drop-overlay__inner {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #019256;
    font-size: 13px;
    font-weight: 500;
}

/* Create-proof picker */
.proof-pick-list {
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #F4F4F6;
    border-radius: 6px;
}
.proof-pick-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
}
.proof-pick-row + .proof-pick-row {
    border-top: 1px solid #F4F4F6;
}
.proof-pick-checkbox {
    width: 16px;
    height: 16px;
    margin: 0;
    flex-shrink: 0;
    cursor: pointer;
}
.proof-pick-name {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
}
.proof-pick-size {
    flex-shrink: 0;
}

/* Delete modal */
.modal-heading {
    color: #3A3A3A;
    font-size: 18px;
    font-weight: 500;
    line-height: 27px;
}
.cancel-btn {
    background-color: #EEF2F5 !important;
}

/* Dropdown (matches global pattern from job-column) */
.dropdown-menu {
    z-index: 1050 !important;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, visibility 0.15s ease;
}
.dropdown-menu.show {
    opacity: 1;
    visibility: visible;
}
</style>
